import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { SECTORS } from "@/lib/omSectors";
import { NAVY, CARD, GOLD, MUTED } from "@/components/om/OmBits";

const ROLES = ["O&M Manager", "Project Manager", "Plant Head", "Business Owner", "EPC Contractor", "Consultant", "Other"];
const SCALES = ["Below 10 Crore", "10 to 50 Crore", "50 to 200 Crore", "Above 200 Crore"];
const TIMELINES = ["Immediately", "Within 1 month", "Within 3 months", "Just exploring"];

const F = { background: "#0D1B2A", border: "1px solid rgba(201,168,76,0.25)", color: "#fff", borderRadius: 6, height: 38, padding: "0 10px", width: "100%", fontSize: 13, outlineColor: GOLD };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.12em] mb-1.5" style={{ color: MUTED }}>{label}</span>
    {children}
  </label>
);

export const SectorLeadForm = ({ sector }) => {
  const [f, setF] = useState({
    name: "", email: "", phone: "", company: "", role: ROLES[0],
    project_type: sector.name, project_scale: SCALES[1], timeline: TIMELINES[1], message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/leads", {
        ...f, sector: sector.name, source_page: window.location.href,
      });
      setDone(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <section className="max-w-[1280px] mx-auto px-5 sm:px-8 py-12" data-testid="sector-lead-section">
      <h2 className="font-semibold text-2xl" style={{ color: GOLD, letterSpacing: "0.03em" }}>
        Get a Free {sector.name} O&amp;M Walkthrough
      </h2>
      <p className="text-white text-[14px] mt-2 max-w-3xl leading-relaxed">
        See exactly how Plan-Sarthi manages O&amp;M for {sector.name} projects — a 20-minute personalised
        walkthrough, no commitment required.
      </p>

      <div className="mt-6 p-5 sm:p-7 border" style={{ background: CARD, borderColor: "rgba(201,168,76,0.18)", borderRadius: 8 }}>
        {done ? (
          <div className="text-center py-6" data-testid="lead-success-block">
            <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: GOLD }} />
            <h3 className="text-white font-semibold text-xl mt-4">You&apos;re all set, {done.first_name}!</h3>
            <p className="mt-2 text-[14px]" style={{ color: GOLD }}>
              We&apos;ll reach out to {done.email} within 1 business day to schedule your walkthrough.
            </p>
            <p className="mt-3 text-[13px]" style={{ color: MUTED }}>
              In the meantime, explore Plan-Sarthi&apos;s O&amp;M features or start your 14-day free trial.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
              <Link to="/features" className="font-semibold border text-center" style={{ borderColor: GOLD, color: GOLD, borderRadius: 8, padding: "11px 24px" }} data-testid="lead-features-btn">Explore O&amp;M Features</Link>
              <Link to="/signup" className="font-semibold text-center" style={{ background: GOLD, color: NAVY, borderRadius: 8, padding: "12px 24px" }} data-testid="lead-trial-btn">Start Free Trial</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="sector-lead-form">
            <Field label="Full name"><input required value={f.name} onChange={set("name")} style={F} data-testid="lead-name-input" /></Field>
            <Field label="Work email"><input required type="email" value={f.email} onChange={set("email")} style={F} data-testid="lead-email-input" /></Field>
            <Field label="Phone number"><input required type="tel" inputMode="numeric" maxLength={10} placeholder="10-digit mobile" value={f.phone} onChange={set("phone")} style={F} data-testid="lead-phone-input" /></Field>
            <Field label="Company or organisation"><input required value={f.company} onChange={set("company")} style={F} data-testid="lead-company-input" /></Field>
            <Field label="Your role">
              <select required value={f.role} onChange={set("role")} style={F} data-testid="lead-role-select">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Project type">
              <select required value={f.project_type} onChange={set("project_type")} style={F} data-testid="lead-project-type-select">
                {SECTORS.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Project scale">
              <select required value={f.project_scale} onChange={set("project_scale")} style={F} data-testid="lead-scale-select">
                {SCALES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="When do you want to start O&M digitisation?">
              <select required value={f.timeline} onChange={set("timeline")} style={F} data-testid="lead-timeline-select">
                {TIMELINES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Message (optional)">
                <textarea rows={3} value={f.message} onChange={set("message")} placeholder="Tell us about your project or any specific O&M challenges you face" style={{ ...F, height: "auto", padding: 10 }} data-testid="lead-message-input" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={busy} className="w-full font-semibold transition-transform duration-150 active:scale-[0.995] disabled:opacity-70" style={{ background: GOLD, color: NAVY, borderRadius: 8, padding: "13px 28px" }} data-testid="lead-submit-btn">
                {busy ? "Submitting…" : "Request My Free Walkthrough"}
              </button>
              <p className="text-[11px] mt-2.5 text-center" style={{ color: MUTED }}>
                We respect your privacy. Your details are only used to schedule your walkthrough and will never be shared.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
